import { useState, useEffect, useContext } from 'react';
import { Trash2, PlusCircle, FileJson, RefreshCcw, ChevronLeft, Save, Download, Globe } from 'lucide-react';

import { Quiz, Question, Profile } from '../../types/index';
import { addQuiz, updateQuiz } from '../../services/quizService';
import { addMyQuizSelection } from '../../services/myQuizSelectionService';

import { useUserProfile } from '../../hooks/useUserProfile';
import { useQuiz } from '../../hooks/useQuiz';

import { UserContext } from '../../services/user/LoginUserContext';
import { logger } from '../../utils/logger';
import { useMyQuizSelection } from '../../hooks/useMyQuizSelection';
import { v4 as uuidv4 } from 'uuid';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

/**
 * CreateQuizView コンポーネントのProps型定義
 */
interface CreateQuizViewProps {
    handlePageChange: (page: string) => void;
    showMessage: (message: string, onConfirm?: () => void, onCancel?: () => void, allowDismiss?: boolean) => void;
    quizToEditId?: string;
    onQuizUpdated: (updatedQuiz?: Quiz) => void;
}

// カテゴリの最大数と最大文字数を定数で定義
const MAX_CATEGORIES = 4;
// 文字数制限の定数
const MAX_CATEGORY_LENGTH = 6;
const MAX_TITLE_LENGTH = 40;
const MAX_QUESTION_TEXT_LENGTH = 100;
const MAX_OPTION_LENGTH = 64;
const MAX_EXPLANATION_LENGTH = 150;
const DEFAULT_MAX_QUESTIONS = 50; // デフォルトの最大問題数を追加

const CreateQuizView: React.FC<CreateQuizViewProps> = ({
    handlePageChange,
    showMessage,
    quizToEditId,
    onQuizUpdated
}) => {
    const { uid: userId, loading: userContextLoading } = useContext(UserContext);
    //ニックネームをとるために利用
    const { profile, isLoading: profileLoading, error: profileError } = useUserProfile(userId);

    // useQuiz フックを使用してクイズデータをフェッチ
    const { quiz: fetchedQuiz, isLoading: quizLoading, isError: quizError, mutateQuiz: quizMute, updateQuizAndLocalCache } = useQuiz(quizToEditId || '');
    // useMyQuizSelection フックを使用してクイズデータをフェッチ
    const { mutate: myQuizMute } = useMyQuizSelection(userId || '');

    const [title, setTitle] = useState<string>('');
    const [category, setCategory] = useState<string[]>(Array(MAX_CATEGORIES).fill(''));
    const [questions, setQuestions] = useState<Question[]>([{ id: uuidv4(), questionText: '', options: ['', '', '', ''], correctAnswer: '', explanation: '' }]);
    const [isPublic, setIsPublic] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    const isOnline = useOnlineStatus(); // オンライン状態を取得

    // ユーザーの作成可能問題数の上限を決定
    const maxQuestions = profile?.createQuizLimit !== undefined ? profile.createQuizLimit : DEFAULT_MAX_QUESTIONS;

    // 編集モードの場合の初期化 (fetchedQuiz が利用可能になったらstateを更新)
    useEffect(() => {

        if (quizToEditId && fetchedQuiz) { // quizToEditId があり、かつ fetchedQuiz がロードされたら

            setTitle(fetchedQuiz.title || '');

            let initialCategories: string[] = Array(MAX_CATEGORIES).fill('');
            if (Array.isArray(fetchedQuiz.category)) {
                fetchedQuiz.category.slice(0, MAX_CATEGORIES).forEach((cat, index) => {
                    initialCategories[index] = cat ? cat.substring(0, MAX_CATEGORY_LENGTH) : '';
                });
            }
            setCategory(initialCategories);

            // 読み込んだ質問にもIDがない場合に付与
            const initialQuestions = fetchedQuiz.questions && fetchedQuiz.questions.length > 0
                ? fetchedQuiz.questions.map(q => ({
                    ...q,
                    id: q.id || uuidv4() // IDがない場合に付与
                }))
                : [{ id: uuidv4(), questionText: '', options: ['', '', '', ''], correctAnswer: '', explanation: '' }];
            setQuestions(initialQuestions);

            setIsPublic(fetchedQuiz.isPublic !== undefined ? fetchedQuiz.isPublic : false);
        } else if (!quizToEditId) {
            setTitle('');
            setCategory(Array(MAX_CATEGORIES).fill(''));
            setQuestions([{ id: uuidv4(), questionText: '', options: ['', '', '', ''], correctAnswer: '', explanation: '' }]);
            setIsPublic(false);//新規作成時、デフォルトは非公開
        }
    }, [quizToEditId, fetchedQuiz]);

    // Loading状態の表示
    if (userContextLoading || profileLoading || (quizToEditId && quizLoading)) {
        return <div className="flex justify-center items-center h-screen bg-sky-50"><div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-cyan-500"></div><p className="ml-4 text-xl text-cyan-700 font-bold">ユーザー情報またはクイズを読み込み中...</p></div>;
    }
    if (profileError) {
        return <div className="text-center mt-10 text-red-500">ユーザープロフィールの取得に失敗しました。</div>;
    }
    if (quizToEditId && quizError) {
        return <div className="text-center mt-10 text-red-500">クイズデータの読み込みに失敗しました。</div>;
    }
    if (!userId) {
        return <div className="text-center mt-10 text-red-500">ログインが必要です。</div>;
    }
    const userNickname = profile?.nickname || '名無し';

    // カテゴリ入力の変更ハンドラ
    const handleCategoryChange = (index: number, value: string) => {
        const newCategories = [...category];
        newCategories[index] = value.length > MAX_CATEGORY_LENGTH ? value.substring(0, MAX_CATEGORY_LENGTH) : value;
        setCategory(newCategories);
    };

    // 問題を追加
    const handleAddQuestion = () => {
        if (!isOnline) {
            showMessage('現在オフラインのため、問題を追加できません。', () => { }, undefined, false);
            return;
        }
        // 作成可能問題数の上限チェックを追加
        if (questions.length >= maxQuestions) {
            showMessage(`これ以上問題を作成できません。最大${maxQuestions}問までです。`, () => { }, undefined, false);
            return;
        }

        setQuestions([...questions, { id: uuidv4(), questionText: '', options: ['', '', '', ''], correctAnswer: '', explanation: '' }]);
    };

    // 質問内容の変更ハンドラ
    const handleQuestionChange = (index: number, field: keyof Question | 'options', value: string | string[]) => {
        const newQuestions = [...questions];
        if (field === 'options') {
            newQuestions[index].options = (value as string[]).map(opt => opt.substring(0, MAX_OPTION_LENGTH)); // 文字数制限を適用
        } else {
            const stringValue = value as string;
            if (field === 'questionText') {
                (newQuestions[index] as any)[field] = stringValue.substring(0, MAX_QUESTION_TEXT_LENGTH); // 文字数制限を適用
            } else if (field === 'explanation') {
                (newQuestions[index] as any)[field] = stringValue.substring(0, MAX_EXPLANATION_LENGTH); // 文字数制限を適用
            } else {
                (newQuestions[index] as any)[field] = stringValue;
            }
        }
        setQuestions(newQuestions);
    };

    // 問題を削除
    const handleRemoveQuestion = (index: number) => {
        if (questions.length <= 1) {
            showMessage('クイズには最低1問必要です。', () => { }, undefined, false);
            return;
        }
        const newQuestions = questions.filter((_, i) => i !== index);
        setQuestions(newQuestions);
    };

    // クイズの保存ハンドラ
    const handleSaveQuiz = async () => {
        // カテゴリのバリデーションを調整
        const validCategories = category.map(cat => cat.trim()).filter(cat => cat !== '');
        if (!title.trim() || validCategories.length === 0) {
            showMessage('クイズのタイトルとカテゴリを少なくとも1つ入力してください。', () => { }, undefined, false);
            return;
        }
        if (questions.some(q => !q.questionText.trim() || q.options.some(o => !o.trim()) || !q.correctAnswer.trim())) {
            showMessage('すべての質問項目（質問文、選択肢、正解）を埋めてください。', () => { }, undefined, false);
            return;
        }
        if (questions.some(q => !q.options.includes(q.correctAnswer))) {
            showMessage('正解は選択肢のいずれかと一致している必要があります。', () => { }, undefined, false);
            return;
        }

        setIsSaving(true);
        try {
            // 保存前に質問のIDを確実に付与
            const questionsToSave = questions.map(q => ({
                ...q,
                id: q.id || uuidv4() // IDがなければ生成
            }));

            const quizData: Omit<Quiz, 'id' | 'createdAt' | 'updatedAt' | 'authorId' | 'authorNickname'> = {
                title,
                category: validCategories,
                questions: questionsToSave, // IDが付与されたquestionsToSaveを使用
                isPublic,
            };

            //編集モード
            if (quizToEditId) {
                //ニックネームが更新されている可能性があるので、最新のニックネームを反映
                await updateQuiz(quizToEditId, { ...quizData, authorNickname: userNickname });
                await addMyQuizSelection(userId, { ...quizData, id: quizToEditId, authorNickname: userNickname, authorId: userId });

                //Quizのキャッシュを上書き
                updateQuizAndLocalCache({ ...quizData, id: quizToEditId, authorNickname: userNickname, authorId: userId });
                myQuizMute();

                showMessage('クイズが更新されました！', () => onQuizUpdated(fetchedQuiz ? { ...fetchedQuiz, ...quizData, authorNickname: userNickname } as Quiz : undefined), undefined, false);
            } else {
                const createdQuiz = await addQuiz({ ...quizData, authorNickname: userNickname, authorId: userId });

                logger.info("createdQuiz:", createdQuiz);

                onQuizUpdated(createdQuiz);

                await addMyQuizSelection(userId, { ...quizData, id: createdQuiz.id, authorNickname: userNickname, authorId: userId });

                //Quizのキャッシュを上書き
                updateQuizAndLocalCache(createdQuiz);
                myQuizMute();
                showMessage('クイズが保存されました！', () => handlePageChange('home'), undefined, false);
            }
        } catch (error) {
            logger.error("Error saving quiz:", error);
            showMessage('クイズの保存に失敗しました。', () => { }, undefined, false);
        } finally {
            setIsSaving(false);
        }
    };

    // JSONファイルからの読み込みハンドラ
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isOnline) {
            showMessage('現在オフラインのため、ファイルの読み込みはできません。', () => { }, undefined, false);
            return;
        }
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                let content = e.target?.result as string;

                // コメントを除去
                content = content.replace(/\/\*[\s\S]*?\*\/|(?<=[^:])\/\/.*$/gm, '');

                const parsedData = JSON.parse(content);

                let titleUpdated = false;
                let categoryUpdated = false;
                let questionsUpdated = false;

                // タイトルとカテゴリの更新（存在する場合）
                if (typeof parsedData.title === 'string' && parsedData.title.trim() !== '') {
                    setTitle(parsedData.title.trim().substring(0, MAX_TITLE_LENGTH));
                    titleUpdated = true;
                }

                // カテゴリの読み込みロジックを修正
                let loadedCategories: string[] = Array(MAX_CATEGORIES).fill('');
                if (typeof parsedData.category === 'string' && parsedData.category.trim() !== '') {
                    loadedCategories[0] = parsedData.category.trim().substring(0, MAX_CATEGORY_LENGTH);
                    categoryUpdated = true;
                } else if (Array.isArray(parsedData.category)) {
                    parsedData.category.slice(0, MAX_CATEGORIES).forEach((cat: any, index: number) => {
                        if (typeof cat === 'string' && cat.trim() !== '') {
                            loadedCategories[index] = cat.trim().substring(0, MAX_CATEGORY_LENGTH);
                        }
                    });
                    if (loadedCategories.some(cat => cat !== '')) {
                        categoryUpdated = true;
                    }
                }
                if (categoryUpdated) {
                    setCategory(loadedCategories);
                }

                // 問題の更新（存在する場合かつ配列として有効な場合）
                if (Array.isArray(parsedData.questions)) {
                    // 問題数が上限を超えていないかチェック
                    if (parsedData.questions.length > maxQuestions) {
                        showMessage(`JSONファイルの問題数が上限(${maxQuestions}問)を超えています。`, () => { }, undefined, false);
                        return;
                    }

                    const isValidQuestions = parsedData.questions.every((q: any) =>
                        q.hasOwnProperty('questionText') && typeof q.questionText === 'string' &&
                        q.hasOwnProperty('options') && Array.isArray(q.options) && q.options.every((opt: any) => typeof opt === 'string') &&
                        q.options.length === 4 &&
                        q.hasOwnProperty('correctAnswer') && typeof q.correctAnswer === 'string' && q.options.includes(q.correctAnswer) &&
                        q.hasOwnProperty('explanation') && typeof q.explanation === 'string'
                    );

                    if (isValidQuestions) {
                        const questionsWithTrimmedContent = parsedData.questions.map((q: Question) => ({
                            ...q,
                            questionText: q.questionText.substring(0, MAX_QUESTION_TEXT_LENGTH),
                            options: q.options.map(opt => opt.substring(0, MAX_OPTION_LENGTH)),
                            explanation: q.explanation.substring(0, MAX_EXPLANATION_LENGTH),
                            id: q.id || uuidv4() // IDが付与されていない場合の対応
                        }));
                        setQuestions(questionsWithTrimmedContent);
                        questionsUpdated = true;
                        showMessage(`${parsedData.questions.length}問の問題を読み込みました！`, () => { }, undefined, false);
                    } else {
                        showMessage('JSONファイルの「questions」部分のフォーマットが正しくありません。', () => { }, undefined, false);
                    }
                } else if (Array.isArray(parsedData)) {
                    // 問題数が上限を超えていないかチェック
                    if (parsedData.length > maxQuestions) {
                        showMessage(`JSONファイルの問題数が上限(${maxQuestions}問)を超えています。`, () => { }, undefined, false);
                        return;
                    }

                    const isValidQuestions = parsedData.every((q: any) =>
                        q.hasOwnProperty('questionText') && typeof q.questionText === 'string' &&
                        q.hasOwnProperty('options') && Array.isArray(q.options) && q.options.every((opt: any) => typeof opt === 'string') &&
                        q.options.length === 4 &&
                        q.hasOwnProperty('correctAnswer') && typeof q.correctAnswer === 'string' && q.options.includes(q.correctAnswer) &&
                        q.hasOwnProperty('explanation') && typeof q.explanation === 'string'
                    );
                    if (isValidQuestions) {
                        const questionsWithTrimmedContent = parsedData.map((q: Question) => ({
                            ...q,
                            questionText: q.questionText.substring(0, MAX_QUESTION_TEXT_LENGTH),
                            options: q.options.map(opt => opt.substring(0, MAX_OPTION_LENGTH)),
                            explanation: q.explanation.substring(0, MAX_EXPLANATION_LENGTH),
                            id: q.id || uuidv4() // IDが付与されていない場合の対応
                        }));
                        setQuestions(questionsWithTrimmedContent);
                        questionsUpdated = true;
                        showMessage(`${parsedData.length}問の問題を読み込みました！`, () => { }, undefined, false);
                    } else {
                        showMessage('JSONファイルが問題の配列形式ですが、フォーマットが正しくありません。', () => { }, undefined, false);
                    }
                }

                // どのデータも更新されなかった場合のメッセージ
                if (!titleUpdated && !categoryUpdated && !questionsUpdated) {
                    showMessage('JSONファイルから有効なクイズ情報が読み込まれませんでした。ファイル形式を確認してください。', () => { }, undefined, false);
                }

            } catch (error) {
                logger.error("Error parsing JSON file:", error);
                showMessage('JSONファイルの読み込みに失敗しました。ファイルの内容が不正な形式です。', () => { }, undefined, false);
            }
        };
        reader.readAsText(file);
    };

    // サンプルJSONダウンロードハンドラ
    const handleDownloadSample = () => {
        if (!isOnline) {
            showMessage('現在オフラインのため、ファイルのダウンロードはできません。', () => { }, undefined, false);
            return;
        }
        const sampleJson = {
            "title": "クイズのタイトル",
            "category": ["カテゴリ1", "カテゴリ2"],
            "isPublic": true,
            "description": "これはクイズの説明文を記載。",
            "questions": [
                {
                    "questionText": "日本の首都はどこですか？",
                    "options": ["大阪", "東京", "京都", "名古屋"],
                    "correctAnswer": "東京",
                    "explanation": "東京は日本の最大の都市であり、政治、経済、文化の中心です。"
                },
                {
                    "questionText": "光合成に必要な主な要素は何ですか？",
                    "options": ["酸素", "二酸化炭素", "窒素", "水素"],
                    "correctAnswer": "二酸化炭素",
                    "explanation": "植物は二酸化炭素と水を利用して光合成を行います。"
                }
            ]
        };
        const jsonString = JSON.stringify(sampleJson, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'quiz_sample.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // クイズのJSONダウンロードハンドラを新規追加
    const handleDownloadQuiz = () => {
        if (!isOnline) {
            showMessage('現在オフラインのため、クイズのダウンロードはできません。', () => { }, undefined, false);
            return;
        }

        // バリデーションチェック
        const validCategories = category.map(cat => cat.trim()).filter(cat => cat !== '');
        if (!title.trim() || validCategories.length === 0) {
            showMessage('クイズのタイトルとカテゴリを少なくとも1つ入力してください。', () => { }, undefined, false);
            return;
        }
        if (questions.some(q => !q.questionText.trim() || q.options.some(o => !o.trim()) || !q.correctAnswer.trim())) {
            showMessage('すべての質問項目（質問文、選択肢、正解）を埋めてください。', () => { }, undefined, false);
            return;
        }
        if (questions.some(q => !q.options.includes(q.correctAnswer))) {
            showMessage('正解は選択肢のいずれかと一致している必要があります。', () => { }, undefined, false);
            return;
        }

        setIsDownloading(true);

        try {
            // ダウンロード用にクイズデータから不要なIDを削除
            const quizDataForDownload = {
                title,
                category: validCategories,
                isPublic,
                questions: questions.map(({ id, ...rest }) => rest)
            };
            const jsonString = JSON.stringify(quizDataForDownload, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${title.trim() || 'my_quiz'}_quiz.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            showMessage('クイズをJSONファイルとしてダウンロードしました！', () => { }, undefined, false);
        } catch (error) {
            logger.error("Error downloading quiz:", error);
            showMessage('クイズのダウンロードに失敗しました。', () => { }, undefined, false);
        } finally {
            setIsDownloading(false);
        }
    };

    const isSaveButtonDisabled = isSaving || !isOnline;

    return (
        <div className="p-4 sm:p-8 max-w-4xl mx-auto">
            <div className="flex items-center mb-8">
                <button
                    onClick={() => handlePageChange(quizToEditId ? 'findQuizzes' : 'home')}
                    className="p-2 rounded-full hover:bg-gray-200"
                    disabled={!isOnline} // オフライン時は無効化
                >
                    <ChevronLeft size={28} />
                </button>
                <h1 className="text-3xl font-bold text-gray-800 ml-4">
                    {quizToEditId ? 'クイズを編集' : `クイズをつくる (最大${maxQuestions}問)`}
                    {/* canDlQuiz が true かつ編集モードのときのみ表示 */}
                    {quizToEditId && profile?.canDlQuiz && (
                            <div className="text-sm text-gray-500 font-mono bg-gray-100 px-3 py-1 rounded-md">
                                クイズID: <span className="font-bold text-gray-700">{quizToEditId}</span>
                            </div>
                    )}
                </h1>
            </div>
            {/* オフライン時にメッセージを表示 */}
            {!isOnline && (
                <div className="mb-6 p-4 rounded-lg font-semibold text-center bg-yellow-100 text-yellow-800 border border-yellow-200">
                    <p>現在オフラインです。</p>
                    <p>この機能はオンライン時にご利用いただけます。</p>
                </div>
            )}


            <div className="bg-white p-6 rounded-2xl shadow-xl space-y-6 mb-8">
                <div>
                    <label className="text-lg font-semibold text-gray-700">クイズのタイトル</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                        maxLength={MAX_TITLE_LENGTH}
                        disabled={!isOnline} // オフライン時は無効化
                    />
                </div>
                <div>
                    <label className="text-lg font-semibold text-gray-700">カテゴリ (最大{MAX_CATEGORIES}つ)</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                        {category.map((cat, index) => (
                            <input
                                key={index}
                                type="text"
                                value={cat}
                                onChange={(e) => handleCategoryChange(index, e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                                placeholder={`カテゴリ ${index + 1} (最大${MAX_CATEGORY_LENGTH}文字)`}
                                maxLength={MAX_CATEGORY_LENGTH}
                                disabled={!isOnline} // オフライン時は無効化
                            />
                        ))}
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <input
                        type="checkbox"
                        id="isPublic"
                        checked={isPublic}
                        onChange={(e) => setIsPublic(e.target.checked)}
                        className="h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                        disabled={!isOnline} // オフライン時は無効化
                    />
                    <label htmlFor="isPublic" className="text-gray-700 flex items-center">
                        <Globe color="#2ECC71" size={20} aria-label="公開クイズ" />このクイズを公開する
                    </label>
                </div>
            </div>
            {!quizToEditId && ( // 編集モードではない場合のみ表示
                <div className="bg-white p-6 rounded-2xl shadow-xl space-y-4 mb-8">
                    <h3 className="text-xl font-bold text-gray-700 flex items-center">
                        <FileJson className="mr-2 text-sky-500" /> JSONファイルから問題を読み込む
                    </h3>
                    <p className="text-sm text-gray-600">
                        指定されたフォーマットのJSONファイルを選択して、問題やクイズ情報を一括で読み込むことができます。
                        タイトル、カテゴリ、問題のいずれかまたは全てを含めることができます。コメント（`//` や `/* */`）も無視されます。
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100 flex-grow"
                            disabled={!isOnline} // オフライン時は無効化
                        />
                        <button
                            onClick={handleDownloadSample}
                            className="flex items-center justify-center py-2 px-4 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition shrink-0"
                            disabled={!isOnline} // オフライン時は無効化
                        >
                            <Download size={20} className="mr-2" /> フォーマットダウンロード
                        </button>
                    </div>
                </div>
            )}
            {/* 新しいダウンロードボタンのセクション */}
            {profile?.canDlQuiz && (
                <div className="bg-white p-6 rounded-2xl shadow-xl space-y-4 mb-8">
                    <h3 className="text-xl font-bold text-gray-700 flex items-center">
                        <Download className="mr-2 text-sky-500" /> クイズをJSONファイルでダウンロード
                    </h3>
                    <p className="text-sm text-gray-600">
                        現在作成中のクイズをJSON形式でダウンロードします。
                    </p>
                    <button
                        onClick={handleDownloadQuiz}
                        disabled={!isOnline || isDownloading}
                        className={`flex items-center py-2 px-4 bg-gray-200 text-gray-700 font-semibold rounded-lg transition ${!isOnline || isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-300'}`}
                    >
                        {isDownloading ? (
                            <><RefreshCcw className="animate-spin mr-2" /> ダウンロード中...</>
                        ) : (
                            <><Download size={20} className="mr-2" /> クイズをダウンロード</>
                        )}
                    </button>
                </div>
            )}


            {questions.map((q, index) => (
                <div key={q.id || index} className="bg-white p-6 rounded-2xl shadow-xl space-y-4 mb-6 relative">
                    <h3 className="text-xl font-bold text-gray-600">問題 {index + 1}</h3>
                    <button
                        onClick={() => handleRemoveQuestion(index)}
                        className="absolute top-4 right-4 p-2 text-red-500 hover:bg-red-100 rounded-full"
                        disabled={!isOnline} // オフライン時は無効化
                    >
                        <Trash2 size={20} />
                    </button>
                    <div>
                        <label className="font-semibold text-gray-600">質問文</label>
                        <textarea
                            value={q.questionText}
                            onChange={(e) => handleQuestionChange(index, 'questionText', e.target.value)}
                            className="w-full mt-1 p-2 border border-gray-300 rounded-lg"
                            rows={2}
                            maxLength={MAX_QUESTION_TEXT_LENGTH}
                            disabled={!isOnline} // オフライン時は無効化
                        ></textarea>
                    </div>
                    <div>
                        <label className="font-semibold text-gray-600">選択肢 (4つ)</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                            {[0, 1, 2, 3].map(optIndex => (
                                <input
                                    key={optIndex}
                                    type="text"
                                    value={q.options[optIndex] || ''}
                                    onChange={(e) => {
                                        const newOpts = [...q.options];
                                        newOpts[optIndex] = e.target.value;
                                        handleQuestionChange(index, 'options', newOpts);
                                    }}
                                    className="p-2 border border-gray-300 rounded-lg"
                                    placeholder={`選択肢 ${optIndex + 1}`}
                                    maxLength={MAX_OPTION_LENGTH}
                                    disabled={!isOnline} // オフライン時は無効化
                                />
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="font-semibold text-gray-600">正解の選択肢</label>
                        <select
                            value={q.correctAnswer}
                            onChange={(e) => handleQuestionChange(index, 'correctAnswer', e.target.value)}
                            className="w-full mt-1 p-2 border border-gray-300 rounded-lg bg-white"
                            disabled={!isOnline} // オフライン時は無効化
                        >
                            <option value="">正解を選んでください</option>
                            {q.options.filter(o => o?.trim() !== '').map((opt, optIndex) => (
                                <option key={optIndex} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="font-semibold text-gray-600">解説文 (任意)</label>
                        <textarea
                            value={q.explanation}
                            onChange={(e) => handleQuestionChange(index, 'explanation', e.target.value)}
                            className="w-full mt-1 p-2 border border-gray-300 rounded-lg"
                            rows={2}
                            maxLength={MAX_EXPLANATION_LENGTH}
                            disabled={!isOnline} // オフライン時は無効化
                        ></textarea>
                    </div>
                </div>
            ))}
            <div className="flex justify-center mt-6">
                <button
                    onClick={handleAddQuestion}
                    className="flex items-center py-2 px-4 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
                    disabled={!isOnline} // オフライン時は無効化
                >
                    <PlusCircle size={20} className="mr-2" /> 問題を追加
                </button>
            </div>
            <div className="mt-8 flex justify-end">
                <button
                    onClick={handleSaveQuiz}
                    disabled={isSaveButtonDisabled}
                    className={`
                        flex items-center py-3 px-8 text-white font-bold rounded-full shadow-lg transition-all transform
                        ${isSaveButtonDisabled
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-custom-gradient from-sky-400 to-cyan-400 hover:from-sky-500 hover:to-cyan-500 hover:scale-105'
                        }
                    `}
                >
                    {isSaving ? (
                        <><RefreshCcw className="animate-spin mr-2" /> 保存中...</>
                    ) : (
                        <><Save size={20} className="mr-2" /> このクイズを保存する</>
                    )}
                </button>
            </div>
        </div>
    );
};

export default CreateQuizView;